import type { UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { RotateCcw, Send, Trash2 } from "lucide-react";
import { Button, IconButton, Typography } from "@akuna/ui";
import { ProductSearch, type PdvItem } from "@/components/erp/pos/ProductSearch";
import type { ComandaLine, ReservationFormValues } from "./reservationForm";
import type { AutomaticStayLine } from "./reservationBilling";
import { ComandaSectionLayout } from "./ComandaSectionLayout";
import { ComandaLineRow } from "./ComandaLineRow";
import { buildComandaQuoteText, openComandaQuoteOnWhatsApp, type QuoteLineInput, type QuoteLineStatus } from "./comandaQuoteText";

interface ReservationComandaProps {
  form: UseFormReturn<ReservationFormValues>;
  petName: string;
  ownerPhone: string | null;
  moduleLabel: string;
  dateLabel: string;
  automaticLines?: AutomaticStayLine[];
  onRemoveAutomaticLine?: (line: AutomaticStayLine) => void;
}

/** Comanda POS da reserva: busca de itens (mesmo componente de vendas), linhas editáveis, total e orçamento por WhatsApp. */
export function ReservationComanda({ form, petName, ownerPhone, moduleLabel, dateLabel, automaticLines = [], onRemoveAutomaticLine }: ReservationComandaProps) {
  const { i18n, t } = useTranslation("daycareHotel");
  const locale = i18n.language.startsWith("pt") ? "pt-BR" : "en-US";
  const lines = form.watch("comanda") ?? [];
  // Cobertura pelo plano é uma regra de cálculo, não uma cobrança. Ela não
  // deve aparecer como uma linha zerada na comanda.
  const displayedAutomaticLines = automaticLines.filter((line) => line.status !== "paid_by_plan");
  const visibleLines = [...displayedAutomaticLines, ...lines];
  const visibleTotal = visibleLines.reduce((sum, line) => sum + line.quantity * line.unitPrice, 0);
  const hasVisibleLines = visibleLines.length > 0;
  const currency = (value: number) =>
    new Intl.NumberFormat(locale, { style: "currency", currency: "BRL" })
      .format(Number.isFinite(value) ? value : 0);

  const setLines = (next: ComandaLine[]) => form.setValue("comanda", next, { shouldValidate: true });

  const waiveKeyFor = (line: AutomaticStayLine): "plan" | "daily" | null =>
    line.status === "plan_charge" ? "plan" : line.status === "charged" ? "daily" : null;

  const toggleWaive = (key: "plan" | "daily") => {
    const current = form.getValues("waivedStayCharges") ?? {};
    form.setValue("waivedStayCharges", { ...current, [key]: !current[key] }, { shouldValidate: true });
  };

  const addExtra = (product: PdvItem) => {
    const existing = lines.findIndex((line) => line.itemId === product.id && line.billingRole === "extra");
    if (existing >= 0) {
      const next = [...lines];
      next[existing] = {
        ...next[existing],
        quantity: next[existing].quantity + 1,
      };
      setLines(next);
      return;
    }
    setLines([...lines, {
      itemId: product.id,
      itemName: product.name,
      quantity: 1,
      unitPrice: product.sale_price ?? 0,
      billingRole: "extra",
    }]);
  };

  const updateLine = (index: number, patch: Partial<ComandaLine>) => {
    const next = [...lines];
    next[index] = { ...next[index], ...patch };
    setLines(next);
  };

  const sendQuote = () => {
    const quoteLines: QuoteLineInput[] = [...displayedAutomaticLines, ...lines].map((line) => ({
      itemName: line.itemName ?? "",
      quantity: line.quantity ?? 0,
      unitPrice: line.unitPrice ?? 0,
      status: "status" in line ? (line.status as QuoteLineStatus | undefined) : undefined,
    }));
    const text = buildComandaQuoteText({
      petName,
      moduleLabel,
      dateLabel,
      lines: quoteLines,
      total: visibleTotal,
      header: t("quoteHeader"),
      totalLabel: t("totalReservation"),
      paidByPlanLabel: t("planChoice.paidByPlan"),
      priceNotConfiguredLabel: t("planChoice.dailyRateNotConfiguredShort"),
      formatCurrency: currency,
    });
    openComandaQuoteOnWhatsApp({ phone: ownerPhone, text });
  };

  const renderLine = (line: ComandaLine, index: number) => <ComandaLineRow key={`${line.itemId ?? "manual"}-${index}`} line={{ itemName: line.itemName, quantity: line.quantity, unitPrice: line.unitPrice }} onChange={(patch) => updateLine(index, patch)} onRemove={() => setLines(lines.filter((_, i) => i !== index))} />;

  const extraLines = lines
    .map((line, index) => ({ line, index }))
    .filter(({ line }) => line.billingRole !== "stay");

  return (
    <ComandaSectionLayout title={t("comanda")} action={<Button size="sm" variant="outline" disabled={!hasVisibleLines} onClick={sendQuote}>
          <Send size={15} className="mr-1.5" />
          {t("sendQuoteWhatsApp")}
        </Button>} search={<ProductSearch onSelectProduct={addExtra} popoverPortalled={false} />} totalLabel={t("totalReservation")} total={currency(visibleTotal)}>

      {!hasVisibleLines ? (
        <Typography asChild variant="body-sm">
          <p className="mx-4 my-3 rounded-lg border border-dashed border-border px-3 py-6 text-center text-muted-foreground">
            {t("comandaEmpty")}
          </p>
        </Typography>
      ) : (
        <div>
          {displayedAutomaticLines.length > 0 ? <ul>{displayedAutomaticLines.map((line) => (
            <li key={`automatic-${line.itemName}-${line.status}`} className="flex flex-wrap items-center justify-between gap-2 border-b border-border px-4 py-3 last:border-b-0">
              <div className="min-w-0">
                <Typography asChild variant="body-sm">
                  <span className="block font-medium text-foreground">{line.itemName}</span>
                </Typography>
              </div>
              <div className="flex items-center justify-end gap-2 text-right">
                <Typography asChild variant="body-sm">
                  <span className="tabular-nums text-muted-foreground">
                    {line.waived
                      ? t("courtesy")
                      : line.status === "paid_by_plan"
                        ? t("planChoice.paidByPlan")
                        : line.status === "not_configured"
                          ? t("planChoice.dailyRateNotConfiguredShort")
                          : `${line.quantity} × ${currency(line.unitPrice)}`}
                  </span>
                </Typography>
                {line.status !== "not_configured" && !line.waived ? (
                  <Typography asChild variant="body-sm">
                    <span className="font-semibold tabular-nums text-foreground">
                      {currency(line.quantity * line.unitPrice)}
                    </span>
                  </Typography>
                ) : null}
                {waiveKeyFor(line) ? (
                  <IconButton
                    size="sm"
                    variant="ghost"
                    label={line.waived ? t("charge") : t("doNotCharge")}
                    onClick={() => line.waived ? toggleWaive(waiveKeyFor(line)!) : onRemoveAutomaticLine?.(line)}
                  >
                    {line.waived ? <RotateCcw size={14} /> : <Trash2 size={14} />}
                  </IconButton>
                ) : null}
              </div>
            </li>
          ))}</ul> : null}
          {extraLines.length > 0 && <ul>{extraLines.map(({ line, index }) => renderLine(line, index))}</ul>}
        </div>
      )}

    </ComandaSectionLayout>
  );
}
